import { cn } from 'lib/utils'
import { useId } from 'react'
import styles from './liquid-logo-reveal.module.css'

/** Liquid fill tiers before the complete frame. */
export const FILL_STEPS = 5

/** Includes the full-complete frame (index 6). */
export const TOTAL_STEPS = 6

/** Default pin size for onboarding chrome (mobile 36px / desktop 64px). */
export const STEPPER_SIZE_CLASS = 'h-9 w-9 lg:h-16 lg:w-16'

export interface LogoStepProgressProps {
  /** Build stage 0–5 = rising liquid; 6 = complete (full + calmer halo). */
  step: number
  className?: string
  /** Wave/bubble/halo motion. Off for static grids (e.g. preview stage matrix). */
  animate?: boolean
  /** Gentler liquid rise (onboarding hub). */
  smoothFill?: boolean
  /** The soft radial glow behind the mark. Off for small sizes (e.g. the mobile
   * pin) where the glow washes the tiny mark into a blur. */
  halo?: boolean
  /** The dark rounded chip behind the mark. Off for the mobile pin, where a dark
   * chip + dark T reads as a muddy square — there it floats bare on the canvas. */
  chip?: boolean
}

const VIEW_HEIGHT = 536
const CHIP_FILL = '#0a0c00' // --ink-on-acid; inline on wrapper (see TakaLogo.tsx)

const T_PATHS = [
  'M289.587 520.899C289.587 528.93 283.076 535.441 275.045 535.441H121.649C113.618 535.441 107.107 528.93 107.107 520.899V270.451C107.107 262.42 100.597 255.909 92.5658 255.909H14.5416C6.51051 255.909 0 249.399 0 241.368V93.283C0 85.2518 6.5105 78.7413 14.5416 78.7413H79.3388V162.626C79.3388 170.657 85.8493 177.168 93.8805 177.168H171.905C179.936 177.168 186.446 183.678 186.446 191.71V442.158C186.446 450.189 192.957 456.7 200.988 456.7H289.587V520.899Z',
  'M480 241.368C480 249.399 473.489 255.909 465.458 255.909H383.467C375.436 255.909 368.926 262.42 368.926 270.451V442.158C368.926 450.189 362.415 456.7 354.384 456.7H289.587V191.71C289.587 183.678 296.097 177.168 304.128 177.168H386.12C394.151 177.168 400.661 170.657 400.661 162.626V93.2829C400.661 85.2518 394.151 78.7413 386.12 78.7413H79.3388V14.5416C79.3388 6.51052 85.8493 0 93.8805 0H465.458C473.489 0 480 6.5105 480 14.5416V241.368Z',
] as const

const WAVE_PATH =
  'M -480 24 q 60 -15 120 0 t 120 0 t 120 0 t 120 0 t 120 0 t 120 0 t 120 0 t 120 0 t 120 0 t 120 0 t 120 0 V 760 H -480 Z'

function clampStep(step: number): number {
  return Math.min(TOTAL_STEPS, Math.max(0, step))
}

function fillProgress(step: number): number {
  const s = clampStep(step)
  if (s === 0) return 0
  return Math.min(1, s / FILL_STEPS)
}

function levelY(progress: number): number {
  return VIEW_HEIGHT * (1 - progress) - 20 * progress
}

export function LogoStepProgress({
  step,
  className,
  animate = true,
  halo = true,
  chip = true,
  smoothFill = false,
}: LogoStepProgressProps) {
  const uid = useId().replace(/[^a-zA-Z0-9_-]/g, '')
  const clipId = `lr-clip-${uid}`
  const waveId = `lr-wave-${uid}`

  const buildStep = clampStep(step)
  const complete = buildStep === TOTAL_STEPS
  const waterY = levelY(fillProgress(buildStep))

  return (
    <div
      className={cn(
        'relative overflow-visible',
        chip && 'rounded-[8px]',
        styles.reveal,
        !animate && styles.static,
        complete && styles.complete,
        className
      )}
      style={chip ? { backgroundColor: CHIP_FILL } : undefined}
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={TOTAL_STEPS}
      aria-valuenow={buildStep}
      aria-valuetext={complete ? 'Complete' : `Step ${buildStep} of ${TOTAL_STEPS}`}
      aria-label="Logo fill progress"
    >
      {halo && <div className={cn(styles.halo, !chip && styles.haloTight)} aria-hidden />}
      <svg
        className={styles.svg}
        viewBox="0 0 480 536"
        aria-hidden
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <clipPath id={clipId} clipPathUnits="userSpaceOnUse">
            {T_PATHS.map((d, i) => (
              <path key={i} d={d} />
            ))}
          </clipPath>
          <path id={waveId} d={WAVE_PATH} />
        </defs>

        <g clipPath={`url(#${clipId})`}>
          <rect className={styles.base} x={0} y={0} width={480} height={536} />
          <g
            className={cn(styles.water, smoothFill && styles.waterSmooth)}
            style={{ transform: `translateY(${waterY.toFixed(1)}px)` }}
          >
            <use className={styles.waveBack} href={`#${waveId}`} />
            <use className={styles.waveFront} href={`#${waveId}`} />
            <circle className={styles.bubble} cx={150} cy={430} r={7} />
            <circle className={cn(styles.bubble, styles.bubble2)} cx={250} cy={400} r={5} />
            <circle className={cn(styles.bubble, styles.bubble3)} cx={330} cy={440} r={6} />
          </g>
        </g>
      </svg>
    </div>
  )
}
